import React, { useState, useEffect, useCallback } from 'react';

const BotControlsOverlay = ({ onSendControl, onOpenMenu, isConnected }) => {
  const [activeKeys, setActiveKeys] = useState({
    forward: false,
    back: false,
    left: false,
    right: false,
    jump: false,
    sneak: false,
    sprint: false
  });

  const handleKeyChange = useCallback((key, state) => {
    setActiveKeys(prev => {
      if (prev[key] === state) return prev;
      if (onSendControl && isConnected) {
        onSendControl(key, state);
      }
      return { ...prev, [key]: state };
    });
  }, [onSendControl, isConnected]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't capture when typing in inputs/textareas
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;

      switch (e.code) {
        case 'KeyW':
        case 'ArrowUp':
          handleKeyChange('forward', true);
          break;
        case 'KeyS':
        case 'ArrowDown':
          handleKeyChange('back', true);
          break;
        case 'KeyA':
        case 'ArrowLeft':
          handleKeyChange('left', true);
          break;
        case 'KeyD':
        case 'ArrowRight':
          handleKeyChange('right', true);
          break;
        case 'Space':
          e.preventDefault();
          handleKeyChange('jump', true);
          break;
        case 'ShiftLeft':
        case 'ShiftRight':
          handleKeyChange('sneak', true);
          break;
        case 'ControlLeft':
        case 'ControlRight':
          handleKeyChange('sprint', true);
          break;
        case 'KeyE':
          if (onOpenMenu && isConnected) {
            onOpenMenu();
          }
          break;
        default:
          break;
      }
    };

    const handleKeyUp = (e) => {
      if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;

      switch (e.code) {
        case 'KeyW':
        case 'ArrowUp':
          handleKeyChange('forward', false);
          break;
        case 'KeyS':
        case 'ArrowDown':
          handleKeyChange('back', false);
          break;
        case 'KeyA':
        case 'ArrowLeft':
          handleKeyChange('left', false);
          break;
        case 'KeyD':
        case 'ArrowRight':
          handleKeyChange('right', false);
          break;
        case 'Space':
          handleKeyChange('jump', false);
          break;
        case 'ShiftLeft':
        case 'ShiftRight':
          handleKeyChange('sneak', false);
          break;
        case 'ControlLeft':
        case 'ControlRight':
          handleKeyChange('sprint', false);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyChange, onOpenMenu, isConnected]);

  const renderKeyButton = (label, keyName, icon = null, extraClasses = '') => {
    const isActive = activeKeys[keyName];
    return (
      <button
        type="button"
        onMouseDown={() => handleKeyChange(keyName, true)}
        onMouseUp={() => handleKeyChange(keyName, false)}
        onTouchStart={() => handleKeyChange(keyName, true)}
        onTouchEnd={() => handleKeyChange(keyName, false)}
        className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl font-mono text-xs font-bold flex flex-col items-center justify-center transition-all select-none border cursor-pointer active:scale-90 ${
          isActive
            ? 'bg-gradient-to-b from-purple-600 to-indigo-600 border-purple-400 text-white shadow-lg shadow-purple-500/40 translate-y-0.5'
            : 'bg-[#12121e]/80 hover:bg-[#1c1c2e] border-purple-500/20 hover:border-purple-500/40 text-gray-200 hover:text-white shadow-sm'
        } ${extraClasses}`}
      >
        {icon ? <i className={`fas ${icon} text-[10px] mb-0.5 opacity-70`}></i> : null}
        <span>{label}</span>
      </button>
    );
  };

  return (
    <div className="bg-[#0c0c14]/90 backdrop-blur-md rounded-2xl border border-purple-500/20 p-2.5 sm:p-4 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-3">
      {/* Directional WASD Pad */}
      <div className="flex items-center justify-center gap-3">
        <div className="flex flex-col items-center gap-1">
          {renderKeyButton('W', 'forward', 'fa-chevron-up')}
          <div className="flex items-center gap-1">
            {renderKeyButton('A', 'left', 'fa-chevron-left')}
            {renderKeyButton('S', 'back', 'fa-chevron-down')}
            {renderKeyButton('D', 'right', 'fa-chevron-right')}
          </div>
        </div>
        <div className="hidden sm:flex flex-col gap-0.5 text-[10px] text-gray-400 font-mono pl-1">
          <span className="flex items-center gap-1.5"><kbd className="px-1 py-0.5 bg-white/10 rounded text-[9px] text-gray-300">W</kbd> Вперед</span>
          <span className="flex items-center gap-1.5"><kbd className="px-1 py-0.5 bg-white/10 rounded text-[9px] text-gray-300">S</kbd> Назад</span>
          <span className="flex items-center gap-1.5"><kbd className="px-1 py-0.5 bg-white/10 rounded text-[9px] text-gray-300">A / D</kbd> Влево / Вправо</span>
        </div>
      </div>

      {/* Action Buttons: Jump, Sneak */}
      <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
        {/* Jump */}
        <button
          type="button"
          onMouseDown={() => handleKeyChange('jump', true)}
          onMouseUp={() => handleKeyChange('jump', false)}
          onTouchStart={() => handleKeyChange('jump', true)}
          onTouchEnd={() => handleKeyChange('jump', false)}
          className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl border text-xs font-bold font-mono transition-all flex items-center justify-center gap-2 select-none cursor-pointer active:scale-95 min-h-[40px] ${
            activeKeys.jump
              ? 'bg-cyan-600 border-cyan-400 text-white shadow-cyan-500/40 shadow-lg'
              : 'bg-[#12121e]/80 hover:bg-[#1c1c2e] border-purple-500/20 text-gray-300 hover:text-white'
          }`}
        >
          <i className="fas fa-arrow-up text-cyan-400 text-xs"></i>
          <span>Прыжок <kbd className="hidden xs:inline-block px-1.5 py-0.5 rounded bg-white/10 text-[10px] text-cyan-300 font-normal">Space</kbd></span>
        </button>

        {/* Sneak */}
        <button
          type="button"
          onMouseDown={() => handleKeyChange('sneak', true)}
          onMouseUp={() => handleKeyChange('sneak', false)}
          onTouchStart={() => handleKeyChange('sneak', true)}
          onTouchEnd={() => handleKeyChange('sneak', false)}
          className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl border text-xs font-bold font-mono transition-all flex items-center justify-center gap-2 select-none cursor-pointer active:scale-95 min-h-[40px] ${
            activeKeys.sneak
              ? 'bg-orange-600 border-orange-400 text-white shadow-orange-500/40 shadow-lg'
              : 'bg-[#12121e]/80 hover:bg-[#1c1c2e] border-purple-500/20 text-gray-300 hover:text-white'
          }`}
        >
          <i className="fas fa-person-falling text-orange-400 text-xs"></i>
          <span>Присед <kbd className="hidden xs:inline-block px-1.5 py-0.5 rounded bg-white/10 text-[10px] text-orange-300 font-normal">Shift</kbd></span>
        </button>
      </div>
    </div>
  );
};

export default BotControlsOverlay;
